import React, {useEffect, useState} from "react";
import moment from "moment-timezone";
import FileInput from "../../components/common/form/FileInput";
import {useDispatch, useSelector} from "react-redux";
import {documentUpload, resetDocumentUploadStatus} from "../../store/vendor-slice";
import AlertMessage from "../../components/ui/AlertMessage";
import {Link, useNavigate, useParams} from "react-router-dom";
import logo from "../../assets/images/vibe24-logo.png";
import SubHeader from "../../components/header/SubHeader";
import Button from "../../components/common/button/Button";
import {fetchVendorListForVendorDoc} from "../../store/list-slice";

const timezone = 'Asia/Kolkata';
const VendorDocAttach = () => {
    const params = useParams();
    const dispatch = useDispatch();
    const navigator = useNavigate();
    const [alertInfo, setAlertInfo] = useState(null);
    const {documentUploadStatus, docUploadError} = useSelector(state => state.vendor);
    const path = `/vendor-dashboard/${params.vendorId}/${params.scopeId}`;
    const list = useSelector(state => state.list);
    const {items: vendors = {}} = list.vendorListDoc;

    const [invoiceData, setInvoiceData] = useState([{
        date: '',
        totalAmount: '',
        receipt: '',
    }]);

    //Invoice data errors
    const [invoiceErrors, setInvoiceErrors] = useState([
        {date: '', totalAmount: '', receipt: ''}
    ]);


    // Validate one row
    const validateInvoiceRow = (invoice) => {
        const errors = {};

        if (!invoice?.date) {
            errors.date = "Date is required";
        }

        if (!invoice?.totalAmount) {
            errors.totalAmount = "Total amount is required";
        }

        if (!invoice?.receipt) {
            errors.receipt = "Invoice is required";
        }

        return errors;
    };

    // Blur validation for a single row
    const handleInvoiceBlur = (index) => {
        const row = invoiceData[index];
        const errors = validateInvoiceRow(row);

        setInvoiceErrors(prev => {
            const newErrors = [...prev];
            newErrors[index] = errors;
            return newErrors;
        });

        return Object.keys(errors).length === 0;
    };

    // Validate all rows at once
    const validateAllInvoice = () => {
        const allErrors = invoiceData.map((row) => validateInvoiceRow(row));
        setInvoiceErrors(allErrors); // update once with full error array

        // return overall validity
        return allErrors.every((err) => Object.keys(err).length === 0);
    };

    const handleInvoiceChange = (index, field, value) => {
        setInvoiceData(prev => {
            const updated = [...prev];
            updated[index] = {...updated[index], [field]: value};
            return updated;
        });
    };

    const handleAddRow = () => {
        setInvoiceData(prev => [...prev, {date: '', receipt: ''}]);
        setInvoiceErrors(prev => [...prev, {date: '', receipt: ''}]);
    };

    const handleRemoveRow = (index) => {
        setInvoiceData(prev => prev.filter((_, i) => i !== index));
        setInvoiceErrors(prev => prev.filter((_, i) => i !== index));
    };


    const handleSubmit = (event) => {
        event.preventDefault();

        const areInvoiceValid = validateAllInvoice();
        if (!areInvoiceValid) return;

        const formDataToSend = new FormData();

        // Append documents array
        invoiceData.forEach((doc, index) => {
            // skip empty rows
            const allEmpty = Object.values(doc).every(val => !val);
            if (allEmpty) return;

            Object.entries(doc).forEach(([field, val]) => {
                if (field === "receipt" && val instanceof File) {
                    // file field
                    formDataToSend.append(`document[${index}][${field}]`, val);
                } else {
                    // normal field
                    formDataToSend.append(`document[${index}][${field}]`, val ?? "");
                }
            });
        });

        formDataToSend.append("vendorId", params.vendorId);
        dispatch(documentUpload(formDataToSend))
    }

    useEffect(() => {
        if (documentUploadStatus === 'success') {
            setAlertInfo({
                message: "Document uploaded successfully.",
                variant: "success",
            });

            setTimeout(() => {
                dispatch(resetDocumentUploadStatus());
                navigator("../docHistory")
            }, 1000); // seconds delay
        }
    }, [dispatch, navigator, documentUploadStatus])

    useEffect(() => {
        dispatch(fetchVendorListForVendorDoc({scopeId: params.scopeId}));
    }, [dispatch, params.scopeId]);

    const vendorName = vendors[params.vendorId]?.toUpperCase() || '';

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-5">
                <Link to={path}><img src={logo} alt="logo" height={60} width={120}/></Link>
                <span className="fs-md align-middle bg-dark px-2 rounded-pill text-white py-1 px-4">{vendorName}</span>
            </div>

            <div className="border rounded-3 p-5 bg-white">
                <SubHeader  title="Generate Statement">
                    <Button to="../docHistory" className="btn-sm bg-primary bg-opacity-50">View History</Button>
                </SubHeader>

                {docUploadError && <p className="fs-md text-danger mb-1">{docUploadError}</p>}
                <form onSubmit={handleSubmit}>
                    <div className="rounded-3">
                        <table className="table mobile-responsive m-0">
                            <thead>
                            <tr>
                                <th>S NO.</th>
                                <th>Date</th>
                                <th>Total Amount</th>
                                <th>Receipt</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {invoiceData.map((item, index) => (
                                <tr key={index} className="noActionRow">
                                    <td data-label="S No: ">{index + 1}</td>
                                    <td data-label="Date: ">
                                        <div className="form-group">
                                            <input
                                                className="form-control"
                                                id="date"
                                                name="date"
                                                type="date"
                                                onBlur={() => handleInvoiceBlur(index)}
                                                value={moment(item.date).tz(timezone).format('YYYY-MM-DD')}
                                                onChange={(e) => handleInvoiceChange(index, "date", e.target.value)}
                                            />
                                            {invoiceErrors[index]?.date && (
                                                <div className="invalid-feedback d-block">
                                                    {invoiceErrors[index].date}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td data-label="Total Amount: ">
                                        <div className="form-group">
                                            <input
                                                className="form-control"
                                                id="totalAmount"
                                                name="totalAmount"
                                                type="text"
                                                onBlur={() => handleInvoiceBlur(index)}
                                                value={item.totalAmount}
                                                onChange={(e) => handleInvoiceChange(index, "totalAmount", Number(e.target.value))}
                                            />
                                            {invoiceErrors[index]?.totalAmount && (
                                                <div className="invalid-feedback d-block">
                                                    {invoiceErrors[index].totalAmount}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td data-label="Receipt: ">
                                        <div className="form-group">
                                            <FileInput
                                                name="receipt"
                                                value={item.receipt}
                                                placeholder="Choose an image..."
                                                onBlur={() => handleInvoiceBlur(index)}
                                                onChange={(e) => handleInvoiceChange(index, "receipt", e.target.value || null)}
                                            />
                                            {invoiceErrors[index]?.receipt && (
                                                <div className="invalid-feedback d-block">
                                                    {invoiceErrors[index].receipt}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td>
                                        {/* Add/Remove Buttons */}
                                        <button className="btn btn-outline-success me-2" type="button"
                                                onClick={handleAddRow}>
                                            +
                                        </button>
                                        {invoiceData.length > 1 && (
                                            <button
                                                className="btn btn-outline-danger"
                                                type="button"
                                                onClick={() => handleRemoveRow(index)}
                                            >
                                                −
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                    <button type="submit" className="btn btn-primary position-absolute" style={{bottom: "5%" , right:"40px"}}>
                        Submit
                    </button>
                </form>

                {alertInfo && (
                    <AlertMessage
                        message={alertInfo.message}
                        variant={alertInfo.variant}
                        duration={2000}
                        onClose={() => setAlertInfo(null)}
                    />
                )}
            </div>
        </>
    )
}

export default VendorDocAttach